import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { CustomError, TechnicalError } from '../common/errors';
import Result from '../common/result';
import { Credentials } from '../common/types';
import credentialService from '../services/credential';
import { ClaimsDisplay } from '../services/credential/credentialService';
import { ScopeRequestStatus } from '../services/secureIdentityProvider';
import { useScopeRequest } from './ScopeRequestContext';
import { useValidation } from './ValidationContext';
import Log from '../common/logging';
import { useCredentialStore } from './CredentialStoreContext';

export enum ShareStatus {
  pending = 'PENDING',
  confirmed = 'CONFIRMED',
  inProgress = 'IN_PROGRESS',
  shared = 'SHARED',
  failed = 'FAILED',
}

export type PartnerInfo = {
  name: string;
};

export interface CredentialShareProps {
  confirmShareCredentials(): void;
  getCredentials: () => Promise<Result<CustomError, Credentials>>;
  handleCredentialsShare: (abortController: AbortController) => Promise<Result<CustomError> | undefined>;
  partnerShareInfo: PartnerInfo | null;
  shareStatus: ShareStatus;
  claims: ClaimsDisplay | null;
}

export const CredentialShareContext = createContext<CredentialShareProps>({} as CredentialShareProps);

type CredentialShareProviderProps = {
  children: JSX.Element;
};

/**
 * Provider to fetch and resolve scope request with credentials
 */
export function CredentialShareProvider(props: CredentialShareProviderProps): JSX.Element {
  const { children } = props;
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [claims, setClaims] = useState<ClaimsDisplay | null>(null);
  const [partnerInfo, setPartnerInfo] = useState<PartnerInfo | null>(null);
  const [shareStatus, setShareStatus] = useState<ShareStatus>(ShareStatus.pending);
  const { getCurrentCredentials } = useValidation();
  const scopeRequestContext = useScopeRequest();
  const { scopeRequestCredentials } = useCredentialStore();

  // TODO CM-1832 get partner info from scope request
  useEffect(() => {
    setPartnerInfo({ name: 'Civic' });
  }, []);

  /**
   * Submit the user data, enriched with all currently set metadata
   */
  const getCredentials = useCallback(async (): Promise<Result<CustomError, Credentials>> => {
    let retrievedCredentials: Credentials;
    // First check if we already have credentials, else fetch them
    if (scopeRequestCredentials) {
      retrievedCredentials = scopeRequestCredentials;
      setCredentials(retrievedCredentials);
    } else {
      const credentialsSuccessResult = await getCurrentCredentials();
      retrievedCredentials = credentialsSuccessResult.success();
      setCredentials(retrievedCredentials);
    }

    const retrievedClaims = await credentialService.getDisplayClaimsForCredentials(retrievedCredentials.credentials);
    setClaims(retrievedClaims);
    return Result.successful(retrievedCredentials);
  }, [getCurrentCredentials, scopeRequestCredentials]);

  const confirmShareCredentials = useCallback((): void => {
    setShareStatus(ShareStatus.confirmed);
  }, []);

  const shareCredentials = useCallback(
    async (retrievedCredentials: Credentials): Promise<Result<CustomError>> => {
      setShareStatus(ShareStatus.inProgress);
      const resolveResult = await scopeRequestContext.resolveScopeRequest(retrievedCredentials);
      if (resolveResult.isFailed()) {
        return Result.failed(new TechnicalError('Unexpected error resolving scope request', resolveResult.failure()));
      }

      const resolvedScopeRequest = resolveResult.success();
      const { status } = resolvedScopeRequest;
      switch (status) {
        case ScopeRequestStatus.VERIFICATION_SUCCESS:
          setShareStatus(ShareStatus.shared);
          return Result.noValueSuccess();
        case ScopeRequestStatus.VERIFICATION_FAILED:
          // This will trigger a navigate to the VALIDATION_ERROR route.
          setShareStatus(ShareStatus.failed);
          // verification-failed is not a TechnicalError.
          return Result.noValueSuccess();
        default:
          setShareStatus(ShareStatus.failed);
          return Result.failed(
            new TechnicalError(`Unexpected status response received after resolving scope request: ${status}`),
          );
      }
    },
    [scopeRequestContext],
  );

  const handleCredentialsShare = useCallback(
    async (abortController?: AbortController): Promise<Result<CustomError, boolean>> => {
      Log.debug('abortController?.signal', abortController?.signal.aborted);
      if (
        scopeRequestContext.scopeRequest &&
        scopeRequestContext.scopeRequest?.status !== ScopeRequestStatus.VERIFICATION_SUCCESS
      ) {
        let retrievedCredentials: Credentials;
        // First check if we already have credentials, else fetch them
        if (!credentials) {
          retrievedCredentials = (await getCredentials()).success();
        } else {
          retrievedCredentials = credentials;
        }
        confirmShareCredentials();
        const shareResult = await shareCredentials(retrievedCredentials);
        if (shareResult.isFailed()) {
          // An internal (technical) error occured while resolving the scope request (something other than verification-failed).
          return shareResult;
        }
        // The SR was resolved successfully.
        // The verification may still have failed, but the shareCredentials function would have set the state accordingly in that case.
        return Result.noValueSuccess();
      }
      setShareStatus(ShareStatus.shared);
      return Result.noValueSuccess();
    },
    [confirmShareCredentials, credentials, getCredentials, scopeRequestContext.scopeRequest, shareCredentials],
  );

  const value = useMemo(
    () => ({
      getCredentials,
      confirmShareCredentials,
      partnerShareInfo: partnerInfo,
      shareStatus,
      claims,
      handleCredentialsShare,
    }),
    [getCredentials, confirmShareCredentials, partnerInfo, shareStatus, claims, handleCredentialsShare],
  );

  return <CredentialShareContext.Provider value={value}>{children}</CredentialShareContext.Provider>;
}
